import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import { Input } from "@/shared/components/Input";
import { Button } from "@/shared/components/Button";
import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import type { Categoria } from "@/modules/biblioteca/services/categoriasService";

const LIMITE_NOME = 100;

const formularioSchema = z.object({
  nome: z
    .string()
    .min(2, "Informe um nome para a categoria.")
    .max(LIMITE_NOME, `O nome pode ter no máximo ${LIMITE_NOME} caracteres.`),
});

export type FormularioCategoriaValores = z.infer<typeof formularioSchema>;

interface FormularioCategoriaProps {
  categoriaEmEdicao?: Categoria | null;
  carregando: boolean;
  /** Erro vindo do backend (ex: nome duplicado), exibido dentro do próprio modal. */
  erro?: string | null;
  /** Avisa o componente pai sempre que o formulário tiver alterações não salvas. */
  onDirtyChange?: (sujo: boolean) => void;
  onSubmit: (dados: FormularioCategoriaValores) => void;
}

export function FormularioCategoria({
  categoriaEmEdicao,
  carregando,
  erro,
  onDirtyChange,
  onSubmit,
}: FormularioCategoriaProps) {
  const ehEdicao = !!categoriaEmEdicao;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormularioCategoriaValores>({
    resolver: zodResolver(formularioSchema),
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const nomeAtual = watch("nome") ?? "";

  useEffect(() => {
    if (categoriaEmEdicao) {
      reset({ nome: categoriaEmEdicao.nome });
    }
  }, [categoriaEmEdicao, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1">
        <Input
          label="Nome da categoria"
          maxLength={LIMITE_NOME}
          erro={errors.nome?.message}
          {...register("nome")}
        />
        <p className={`text-right text-xs ${nomeAtual.length >= LIMITE_NOME ? "font-medium text-warning" : "text-muted"}`}>
          {nomeAtual.length}/{LIMITE_NOME}
        </p>
      </div>

      {/* Erro do backend (ex: nome duplicado) — exibido DENTRO do modal, não atrás dele. */}
      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <Button type="submit" carregando={carregando} className="mt-2">
        {ehEdicao ? "Salvar alterações" : "Criar categoria"}
      </Button>
      <BotaoVoltar disabled={carregando} className="-mt-2" />
    </form>
  );
}
